import type {
  ClientId,
  MeasurementReportId,
  EquipmentId,
  InspectionId,
  ProfessionalId
} from "../../shared/kernel/identifiers.js";
import { isCalibrationExpired } from "../ndt/ndt-calibration.js";
import {
  isHighTemperatureMeasurement,
  isValidMaterialFamilyCode,
  materialFamilyLabel,
  ME_MATERIAL_FAMILIES,
  ME_TEMPERATURE_MAX_C,
  ME_TEMPERATURE_MIN_C,
  ME_HIGH_TEMPERATURE_THRESHOLD_C,
  normalizeSearchText,
  parseMeasurementTemperature,
  formatFactor,
  formatMm,
  ME_HIGH_TEMPERATURE_LEGEND,
  METHOD_A_LABEL,
  METHOD_A_MISSING_DATA_MESSAGE,
  METHOD_A_COEFFICIENT_K,
  METHOD_A_REFERENCE_TEMPERATURE_C
} from "./me-thermal-correction.js";
import type { NdtProcedure } from "../ndt/ndt-procedure.js";

/** Re-exports from me-thermal-correction for convenient single import by consumers. */
export {
  isHighTemperatureMeasurement,
  isValidMaterialFamilyCode,
  materialFamilyLabel,
  ME_MATERIAL_FAMILIES,
  ME_TEMPERATURE_MAX_C,
  ME_TEMPERATURE_MIN_C,
  ME_HIGH_TEMPERATURE_THRESHOLD_C,
  normalizeSearchText,
  parseMeasurementTemperature,
  formatFactor,
  formatMm,
  ME_HIGH_TEMPERATURE_LEGEND,
  METHOD_A_LABEL,
  METHOD_A_MISSING_DATA_MESSAGE
} from "./me-thermal-correction.js";

/** Matches the LEGENDA E CRITÉRIOS DE RESULTADO of the official ME PDF (section 05) — do not invent new statuses. */
export type MeLaudo = "A" | "R" | "EC" | "P" | "ADPC" | null;

export interface MeResultPoint {
  /**
   * Internal, immutable row identity. The user-facing "ID DO PONTO" is `id`
   * and may be renamed freely, so nothing may key off it.
   */
  key: string;
  /** "ID DO PONTO" exactly as the user typed it ("P01", "TUB-03-12H"...) — never regenerated after being edited. */
  id: string;
  component: string;
  location: string;
  /** Dimensional values are stored as numbers; "6,20" is presentation only (see me-measurements.ts). */
  nominalThickness: number | null;
  minThickness: number | null;
  /**
   * A espessura que o aparelho indicou.
   *
   * Em medição de ALTA TEMPERATURA (TME > 60 °C) este é o valor Emq —
   * Espessura Medida a Quente —, e a espessura que vale para o laudo e para
   * os cálculos passa a ser `correctedThickness` (Ea — MÉTODO A, RESULTADO
   * APROXIMADO). O valor medido continua aqui, intacto, para rastreabilidade
   * — a correção acrescenta um dado, nunca sobrescreve a leitura de campo.
   */
  actualThickness: number | null;
  /**
   * Ea — Espessura Aproximada, em mm (duas casas). MÉTODO A —
   * RESULTADO APROXIMADO (nunca "PRECISO").
   *
   * Preenchida só quando houve alta temperatura E o MÉTODO A pôde ser
   * calculado (Ea = Emq × FC_A). `null` significa "não calculada", nunca
   * "igual à medida": faltando Vsa, k, Temperatura de Referência (do
   * Procedimento vinculado), Temperatura da Peça ou Emq, o sistema declara a
   * lacuna em vez de aproximar.
   */
  correctedThickness?: number | null;
  /** Retrato do MÉTODO A aplicado a este ponto (null fora da alta temperatura). */
  hotCorrection?: MeHotCorrectionSnapshot | null;
  /**
   * Manual documentary classification (EC / ADPC), kept for the states the
   * legend defines. The dimensional A/R result is derived by resolveLaudo()
   * from the thicknesses and is not stored here.
   */
  laudo: MeLaudo;
  /**
   * Explicit decision that replaces the calculated result — today only
   * R → ADPC. Kept apart from the calculated value so the override stays
   * traceable (calculado vs final).
   */
  laudoOverride?: MeLaudo;
  laudoOverrideReason?: string;
}

/**
 * Brings points saved before the numeric/keyed model into shape: gives each row
 * a stable key and converts thicknesses that were stored as text into
 * numbers. Reading an older report must not silently show empty fields.
 */
export function normalizeMeReportPoints(report: MeReport): MeReport {
  if (!Array.isArray(report.points)) {
    report.points = [];
    return report;
  }

  report.points = report.points.map((raw, index) => {
    const point = raw as any;
    const toNumber = (value: unknown): number | null => {
      if (value === null || value === undefined || value === "") return null;
      if (typeof value === "number") return Number.isFinite(value) ? value : null;
      const parsed = Number(String(value).replace(",", "."));
      return Number.isFinite(parsed) ? parsed : null;
    };

    return {
      ...point,
      key: point.key || `PT-LEGACY-${index}-${point.id || index}`,
      id: point.id ?? "",
      component: point.component ?? "",
      location: point.location ?? "",
      nominalThickness: toNumber(point.nominalThickness),
      minThickness: toNumber(point.minThickness),
      actualThickness: toNumber(point.actualThickness),
      correctedThickness: toNumber(point.correctedThickness),
      laudo: point.laudo ?? null
    } as MeResultPoint;
  });

  return report;
}

/**
 * Situação da correção MÉTODO A de um ponto:
 *
 *   CORRECTED    Vsa, k, Temperatura de Referência e Emq disponíveis — FC_A e Ea calculados
 *   INCOMPLETE   falta algum dado de entrada (Vsa/k/Tref do Procedimento, Temperatura
 *                da Peça ou Emq) — o sistema declara a lacuna, nunca aproxima
 */
export type MeHotCorrectionStatus = "CORRECTED" | "INCOMPLETE";

/**
 * RETRATO do MÉTODO A (aproximado) usado num ponto — congelado na gravação.
 *
 * MÉTODO A — RESULTADO APROXIMADO:
 *   FC_A = (Vsa − k×ΔT) / Vsa
 *   Ea   = Emq × FC_A
 *
 * onde ΔT = Temperatura da Peça − Temperatura de Referência. k (1,0 m/s/°C)
 * e a Temperatura de Referência (30 °C) são CONSTANTES FIXAS do Método A
 * (`METHOD_A_COEFFICIENT_K`/`METHOD_A_REFERENCE_TEMPERATURE_C`, decisão
 * oficial do usuário) — só Vsa vem do Procedimento (NdtProcedure) vinculado
 * ao relatório, pois varia por material. Mesmo sendo constantes hoje, os
 * três continuam gravados aqui ponto a ponto: alterar a constante no código
 * no futuro não pode reescrever o resultado de um relatório já emitido —
 * este retrato guarda os valores efetivamente usados no cálculo daquele dia.
 */
export interface MeHotCorrectionSnapshot {
  method: "METHOD_A";
  status: MeHotCorrectionStatus;
  /** Emq — Espessura Medida a Quente (mm). */
  hotMeasuredThicknessMm: number | null;
  /** Temperatura da peça no momento da medição (°C) — mesmo valor do relatório (Bloco 2). */
  measurementTemperatureC: number | null;
  /** Temperatura de referência (°C) — 30 °C, fixa (`METHOD_A_REFERENCE_TEMPERATURE_C`). */
  referenceTemperatureC: number | null;
  /** ΔT = Temperatura da Peça − Temperatura de Referência. */
  deltaTemperatureC: number | null;
  /** Vsa — velocidade ultrassônica de referência (m/s), do Procedimento vinculado. */
  referenceVelocityMs: number | null;
  /** k — coeficiente de variação — 1,0 m/s/°C, fixo (`METHOD_A_COEFFICIENT_K`). */
  coefficientK: number | null;
  /** FC_A = (Vsa − k×ΔT) / Vsa, precisão total. Null quando não calculado. */
  correctionFactor: number | null;
  /** Ea = Emq × FC_A, precisão total. Null quando não calculado. */
  approximateThicknessMm: number | null;
  materialFamilyCode: string | null;
  materialSpecification: string;
  procedureId: string | null;
  procedureCode: string;
  procedureRevision: string;
  deviceAssetId: string | null;
  transducerAssetId: string | null;
  couplantId: string | null;
  message: string;
  calculatedAt: string;
  calculatedBy: string | null;
}

/**
 * RESUMO da correção de alta temperatura do relatório — o pior estado entre
 * os pontos, quantos foram corrigidos e quais calibrações foram usadas.
 *
 * `status` guarda inclusive os casos em que NÃO houve correção, porque
 * "nenhuma calibração cobria a medição quando este documento foi fechado"
 * é um fato que o relatório precisa conseguir contar depois.
 */
export interface MeThermalCorrectionRecord {
  status:
    | "NOT_APPLICABLE"
    | MeHotCorrectionStatus
    /** @deprecated Motor de coeficientes removido — só aparece em registro antigo. */
    | "RULE_NOT_CONFIGURED"
    /** @deprecated MÉTODO B (calibração) removido — só aparece em registro antigo. */
    | "CALIBRATION_NOT_FOUND"
    /** @deprecated MÉTODO B (calibração) removido — só aparece em registro antigo. */
    | "TEMPERATURE_LIMIT_EXCEEDED"
    /** @deprecated MÉTODO B (calibração) removido — só aparece em registro antigo. */
    | "VERIFICATION_REQUIRED"
    /** @deprecated MÉTODO B (calibração) removido — só aparece em registro antigo. */
    | "CALIBRATION_FAILED";
  method?: "METHOD_A" | "METHOD_B" | null;
  /** "MÉTODO A — RESULTADO APROXIMADO". */
  standardCode?: string | null;
  /** TME — Temperatura da Peça considerada no cálculo. */
  temperatureC: number | null;
  materialFamilyCode: string | null;
  materialFamilyLabel: string;
  materialSpecification?: string;
  /** Procedimento (código) de onde vieram Vsa/k/Temperatura de Referência. */
  procedureCode?: string | null;
  /** @deprecated Método B (calibração) — só aparece em registro antigo. */
  calibrationCodes?: string[];
  correctedPoints?: number;
  pendingPoints?: number;
  /** @deprecated Campos do motor de coeficientes removido (registros antigos). */
  referenceTemperatureC?: number | null;
  /** @deprecated */
  ruleId?: string | null;
  /** @deprecated */
  ruleVersion?: string | null;
  /** @deprecated */
  ruleSource?: string | null;
  /** Mensagem exibida quando não foi possível corrigir. Vazia quando corrigiu. */
  message: string;
  computedAt: string;
  computedByUserId: string | null;
}

export interface MeSignatory {
  role: "INSPETOR" | "PLH" | "FISCALIZACAO";
  professionalId: ProfessionalId | null;
  name: string;
  functionTitle: string;
  credential: string;
  signatureImageId: string | null;
}

/**
 * Situação do ME no próprio módulo:
 *
 *   DRAFT       recém-criado, ainda sem nenhum salvamento
 *   IN_PROGRESS em elaboração (a partir do primeiro salvamento; nunca volta)
 *   FINALIZED   fechado pela aprovação do PLH
 *   SIGNED      assinado (estado legado do módulo, preservado)
 *   VALIDATED   legado, nunca atribuído — mantido só para leitura de registros antigos
 *
 * "EM APROVAÇÃO DO PLH" NÃO é um valor daqui: enquanto o documento está com
 * o PLH, quem sabe disso é o ciclo de revisão central. Guardar esse estado
 * também no módulo criaria duas fontes para o mesmo fato, que podem divergir
 * — o rótulo é derivado em `meWorkflowStatusLabel`.
 */
export type MeReportStatus = "DRAFT" | "IN_PROGRESS" | "VALIDATED" | "SIGNED" | "FINALIZED";

/** Situação exibida ao usuário, combinando o módulo com o ciclo de revisão. */
export function meWorkflowStatusLabel(
  status: MeReportStatus | string,
  reviewStatus?: string | null
): string {
  if (status === "FINALIZED") return "FINALIZADO";
  if (status === "SIGNED") return "ASSINADO";
  if (reviewStatus === "PLH_APPROVED") return "FINALIZADO";
  if (reviewStatus === "AWAITING_PLH_REVIEW" || reviewStatus === "PLH_REVIEWING") return "EM APROVAÇÃO DO PLH";
  if (reviewStatus === "CORRECTION_REQUESTED") return "CORREÇÃO SOLICITADA";
  if (status === "IN_PROGRESS") return "EM ELABORAÇÃO";
  if (status === "VALIDATED") return "VALIDADO";
  if (status === "DRAFT") return "RASCUNHO";
  return String(status);
}

/** Estados em que o módulo considera o ME fechado para edição ordinária. */
export function isMeClosed(status: MeReportStatus | string): boolean {
  return status === "SIGNED" || status === "FINALIZED";
}

export interface MeReport {
  id: MeasurementReportId;
  clientId: ClientId;

  /** "Rel. INSPECT IA nº" — generated once at creation, never regenerated on save. */
  reportNumber: string;
  /** "Rel. cliente nº" — free text, optional, the client's own internal reference. */
  clientReportNumber: string;
  revision: string;
  status: MeReportStatus;

  // 01 — IDENTIFICAÇÃO
  /**
   * VÍNCULO REAL com o Projeto e a Unidade/Área — por ID, não por texto.
   *
   * `projectContract` e `unitArea` são rótulos de apresentação ("TES-2026 —
   * TESTE 01"); o workflow do PLH resolve o responsável por
   * Documento → Projeto → Cliente e precisa do ID. Sem estes campos o
   * relatório mostrava o Projeto na tela e, ao mesmo tempo, era recusado com
   * "documento não está vinculado a um Projeto" — a tela lia o texto e o
   * workflow lia um id que nunca existiu.
   */
  projectId: string | null;
  unitAreaId: string | null;
  projectContract: string;
  unitArea: string;
  equipmentId: EquipmentId | null;
  equipmentTag: string;
  equipmentTypeLabel: string;
  /** Official registry references (WorkOrder / SubWorkOrder) — the report points at them, it does not retype them. */
  osWorkOrderId: string | null;
  osSubWorkOrderId: string | null;
  /** Frozen copies of the codes used in this report, so a later edit in the registry never rewrites an issued document. */
  osNumber: string;
  subOsNumber: string;
  /** Combined "OS / SUB-OS" as printed on the document header. */
  osSubOs: string;
  testDate: string;
  periodStart: string;
  periodEnd: string;
  inspectionId: InspectionId | null;
  inspectorProfessionalId: ProfessionalId | null;
  inspectorName: string;

  // 02 — PARÂMETROS DO ENSAIO
  /** Registered ME procedure this report is bound to (domain/ndt/ndt-procedure.ts); null when filled manually. */
  procedureId: string | null;
  procedureRevision: string;
  /**
   * Every *Id below records WHICH registration was selected; the plain text
   * field next to it records WHAT it said at that moment. The text is what
   * the PDF prints, so a later edit to the master record never rewrites a
   * report that was already issued (regra 19 — snapshot documental).
   */
  referenceNormId: string | null;
  referenceNorm: string;
  acceptanceCriteriaId: string | null;
  acceptanceCriteria: string;
  /**
   * @deprecated Texto livre do modelo antigo. Continua gravado para que
   * nenhum relatório já emitido perca o que dizia, e é a origem da migração
   * para `materialFamilyCode`. Campo oficial hoje: `materialFamilyCode`.
   */
  material: string;
  /** GRUPO DE MATERIAL (aço) — código canônico do catálogo (ME_MATERIAL_FAMILIES). */
  materialFamilyCode: string | null;
  /**
   * MATERIAL ESPECÍFICO ("ASTM A106 Gr.B"), opcional. Uma calibração Método B
   * qualificada por material específico só é aplicada quando esta
   * especificação coincide; a de GRUPO vale para qualquer material do grupo.
   */
  materialSpecification?: string;
  /**
   * Rótulo congelado no momento da escolha ("AÇO CARBONO"). O documento
   * imprime este texto: renomear a família no catálogo não pode reescrever
   * um relatório já emitido (mesma regra dos demais catálogos do ME).
   */
  materialFamilyLabel: string;
  /** One of ME_SURFACE_CONDITIONS (LIXADA | PINTADA). */
  surfaceCondition: string;
  /**
   * @deprecated Texto livre do modelo antigo. Campo oficial hoje:
   * `measurementTemperatureC`. Mantido pelo mesmo motivo de `material`.
   */
  temperature: string;
  /** TME — Temperatura da Medição de Espessura, inteiro de 0 °C ao máximo do perfil técnico (N-2371: 550 °C). */
  measurementTemperatureC: number | null;
  /**
   * @deprecated Referência do motor de coeficientes removido. No Método B as
   * temperaturas de referência são as da CALIBRAÇÃO (fria e quente) e ficam
   * no retrato de cada ponto. Mantido só para ler relatórios antigos.
   */
  calibrationTemperatureC: number | null;
  /**
   * Resumo da correção Método B na última gravação: estado, calibrações
   * usadas, pontos corrigidos/pendentes, quando e por quem. Congelado no
   * snapshot na aprovação.
   */
  thermalCorrection: MeThermalCorrectionRecord | null;
  thermometerCert: string;
  couplantId: string | null;
  couplant: string;
  calibrationBlockAssetId: string | null;
  calibrationBlock: string;
  calibrationBlockMaterial: string;
  calibrationBlockRange: string;
  calibrationBlockCert: string;
  calibrationBlockCertDate: string;
  calibrationBlockCertValidity: string;
  calibrationBlockCertLaboratory: string;
  /** One of ME_MEASUREMENT_TECHNIQUES (domain/ndt/ndt-procedure.ts). */
  measurementTechnique: string;
  /** Free text used only when measurementTechnique is "Outro". */
  measurementTechniqueOther: string;
  /**
   * @deprecated Campo digitado do modelo antigo. O FC NUNCA é digitado: vem
   * da calibração Método B de cada ponto (`points[].hotCorrection`). Mantido
   * só para que relatórios já emitidos não percam o que diziam.
   */
  correctionFactor: string;

  // 03 — APARELHO E TRANSDUTOR
  /** Aparelho de ME-US selected from the END registry (NdtAsset). */
  deviceAssetId: string | null;
  deviceManufacturer: string;
  deviceModel: string;
  deviceSerialNumber: string;
  deviceCalibrationCert: string;
  deviceCalibrationDate: string;
  deviceCalibrationValidity: string;
  deviceCalibrationLaboratory: string;
  /** Transdutor selected from the END registry (NdtAsset). */
  transducerAssetId: string | null;
  transducerManufacturer: string;
  transducerModel: string;
  transducerSerialNumber: string;
  transducerIdentification: string;
  /** Tipo and Frequência are recorded apart — a frequency is never a type (Bloco 17). */
  transducerType: string;
  transducerFrequency: string;
  transducerDimension: string;
  transducerDimensionFrequency: string;

  // 04 — RESULTADOS DAS MEDIÇÕES
  points: MeResultPoint[];

  // 06 — CROQUI / ESQUEMA DE LOCALIZAÇÃO
  croquiImage: string | null;

  // 07 — COMENTÁRIOS E OBSERVAÇÕES (optional — an empty value is valid, never auto-filled)
  comments: string;

  // 08 — TERMO DE RESPONSABILIDADE & SIGNATÁRIOS
  signatories: MeSignatory[];

  /** "Data da emissão" — frozen at the moment of the last save, like the Minuta's DATA DA IMPRESSÃO (docs/RBAC.md, section 15 of CLAUDE.md). */
  issueDate: string;

  clientDisplayName: string;

  signedAt: string | null;
  signedByUserId: string | null;
  signedByProfessionalId: ProfessionalId | null;

  /**
   * DOCUMENTO CONGELADO NA APROVAÇÃO DO PLH.
   *
   * O DocumentModel exatamente como o PLH o aprovou. Enquanto for `null`, o
   * documento é montado ao vivo a cada visualização/PDF. Depois da aprovação
   * é este objeto que vale — a montagem ao vivo resolve a imagem de
   * assinatura de cada Professional no momento da geração, então sem
   * congelar, trocar uma assinatura no cadastro faria um PDF regerado sair
   * diferente do que foi aprovado. Gravado por `finalizeOnPlhApproval`, nunca
   * pela edição.
   */
  frozenSnapshot?: unknown | null;

  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface MeValidationResult {
  valid: boolean;
  errors: string[];
}

/* ══════════════════════════════════════════════════════════════════════
   FAMÍLIA DE MATERIAL, TME E CORREÇÃO TÉRMICA — normalização e recálculo
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Traz um relatório gravado antes destes campos para o modelo atual.
 *
 * A migração do texto livre é DELIBERADAMENTE CONSERVADORA: só adota a
 * família quando o texto antigo corresponde exatamente ao rótulo, ao código
 * ou a um apelido conhecido. "AÇO CARBONO" vira CARBON_STEEL; "A-516 GR60"
 * não vira nada e continua visível no campo legado, para a pessoa escolher.
 * Adivinhar aqui plantaria um material errado num relatório técnico.
 *
 * O texto legado NUNCA é apagado.
 */
export function normalizeMeThermalFields(report: MeReport): MeReport {
  const target = report as MeReport & Record<string, unknown>;

  if (typeof target.materialFamilyCode !== "string" && target.materialFamilyCode !== null) {
    target.materialFamilyCode = null;
  }
  if (typeof target.materialFamilyLabel !== "string") target.materialFamilyLabel = "";
  if (target.calibrationTemperatureC === undefined) target.calibrationTemperatureC = null;
  if (target.thermalCorrection === undefined) target.thermalCorrection = null;
  if (typeof target.materialSpecification !== "string") target.materialSpecification = "";

  // Código que não existe mais no catálogo não é promovido a válido, mas
  // também não é descartado: o rótulo congelado preserva o que foi escolhido.
  if (report.materialFamilyCode && !report.materialFamilyLabel) {
    report.materialFamilyLabel = materialFamilyLabel(report.materialFamilyCode);
  }

  if (!report.materialFamilyCode && typeof report.material === "string" && report.material.trim()) {
    const term = normalizeSearchText(report.material);
    const exact = ME_MATERIAL_FAMILIES.find(family =>
      normalizeSearchText(family.label) === term ||
      normalizeSearchText(family.code) === term ||
      family.aliases.some(alias => normalizeSearchText(alias) === term)
    );
    if (exact) {
      report.materialFamilyCode = exact.code;
      report.materialFamilyLabel = exact.label;
    }
  }

  if (typeof report.measurementTemperatureC !== "number" || !Number.isFinite(report.measurementTemperatureC)) {
    // O texto antigo ("120", "120 °C") vira número quando for um inteiro da
    // faixa; "AMBIENTE" não vira nada e o campo fica por preencher.
    report.measurementTemperatureC = parseMeasurementTemperature(report.temperature);
  }

  return report;
}

/**
 * Recalcula a correção MÉTODO A (aproximado) de cada ponto e o resumo do relatório.
 *
 * É a ÚNICA função que escreve `correctedThickness`, `hotCorrection` e
 * `thermalCorrection`. O serviço a chama ao gravar, de modo que o valor
 * persistido já é o valor final — a tela, o Preview, o PDF e a AVR apenas
 * leem. É por isso que o PDF não recalcula nada: se recalculasse, um
 * Procedimento alterado depois mudaria a reimpressão de um documento aprovado.
 *
 * Por ponto: FC_A = (Vsa − k×ΔT) / Vsa, Ea = Emq × FC_A, onde
 * ΔT = Temperatura da Peça − Temperatura de Referência. k (1,0 m/s/°C) e a
 * Temperatura de Referência (30 °C) são CONSTANTES FIXAS do Método A
 * (`METHOD_A_COEFFICIENT_K`/`METHOD_A_REFERENCE_TEMPERATURE_C`); só Vsa vem
 * do `procedure` (NdtProcedure) passado pelo chamador, por variar por
 * material. Faltando o Procedimento, o Vsa nele, a Temperatura da Peça, ou
 * o Emq do ponto, o ponto fica INCOMPLETE e Ea permanece VAZIA — nunca um
 * valor aproximado por omissão. Qualquer alteração de Emq, TME (Temperatura
 * da Peça) ou do Procedimento vinculado invalida o resultado anterior,
 * porque tudo é resolvido de novo.
 *
 * `procedure` ausente ou nulo = nenhum Procedimento vinculado (ou vinculado
 * mas sem Vsa cadastrado): o resultado é INCOMPLETE, nunca uma correção
 * presumida com valores inventados.
 */
export function recomputeMeThermalCorrection(
  report: MeReport,
  options: {
    now: string;
    userId?: string | null;
    /** Procedimento vinculado ao relatório (`report.procedureId`), de onde vem Vsa. */
    procedure?: NdtProcedure | null;
  }
): MeReport {
  normalizeMeThermalFields(report);

  const temperatureC = report.measurementTemperatureC;
  const materialCode = report.materialFamilyCode;
  const points = Array.isArray(report.points) ? report.points : [];
  const base = {
    temperatureC: temperatureC ?? null,
    materialFamilyCode: materialCode,
    materialFamilyLabel: report.materialFamilyLabel || materialFamilyLabel(materialCode),
    materialSpecification: report.materialSpecification || "",
    computedAt: options.now,
    computedByUserId: options.userId ?? null
  };

  if (!isHighTemperatureMeasurement(temperatureC)) {
    // Voltar para o fluxo convencional precisa APAGAR a Ea antiga: deixá-la
    // gravada faria o laudo continuar decidindo por uma correção que não se
    // aplica mais.
    for (const point of points) {
      point.correctedThickness = null;
      point.hotCorrection = null;
    }
    report.thermalCorrection = { ...base, status: "NOT_APPLICABLE", method: null, standardCode: null, procedureCode: null, correctedPoints: 0, pendingPoints: 0, message: "" };
    return report;
  }

  const procedure = options.procedure ?? null;
  const vsa = typeof procedure?.hotCorrectionReferenceVelocityMs === "number" && Number.isFinite(procedure.hotCorrectionReferenceVelocityMs) && procedure.hotCorrectionReferenceVelocityMs > 0
    ? procedure.hotCorrectionReferenceVelocityMs
    : null;
  // k e Temperatura de Referência são FIXOS (decisão oficial do usuário):
  // 1,0 m/s/°C e 30 °C para todo cálculo NOVO, independentemente do que o
  // Procedimento vinculado tenha gravado em hotCorrectionCoefficientK/
  // hotCorrectionReferenceTemperatureC — esses dois campos do cadastro
  // seguem existindo só para não quebrar a leitura de um Procedimento já
  // salvo (nenhum cálculo novo os lê). Vsa continua vindo do Procedimento:
  // é o único dos três que varia por material.
  const k = METHOD_A_COEFFICIENT_K;
  const tref = METHOD_A_REFERENCE_TEMPERATURE_C;
  // Código curto do procedimento (não o rótulo completo formatado): é o que
  // aparece na tabela do PDF, no retrato de auditoria e alimenta o AVR como
  // calibrationId — um rótulo com título/revisão não caberia nesses usos.
  const procedureCode = procedure?.code || "";

  const statuses: MeHotCorrectionStatus[] = [];

  for (const point of points) {
    const emq = typeof point.actualThickness === "number" && Number.isFinite(point.actualThickness) && point.actualThickness > 0
      ? point.actualThickness
      : null;

    const snapshot = methodASnapshotFor(report, point, emq, temperatureC ?? null, procedure, vsa, k, tref, procedureCode, options);

    point.hotCorrection = snapshot;
    point.correctedThickness = snapshot.status === "CORRECTED" && snapshot.approximateThicknessMm !== null
      // Duas casas: a precisão em que toda espessura do ME é registrada. A
      // Ea em precisão total fica no retrato (`hotCorrection.approximateThicknessMm`).
      ? Math.round(snapshot.approximateThicknessMm * 100) / 100
      : null;
    statuses.push(snapshot.status);
  }

  const corrected = statuses.filter(s => s === "CORRECTED").length;
  const pending = statuses.length - corrected;
  const status: MeThermalCorrectionRecord["status"] = points.length === 0
    ? "INCOMPLETE"
    : pending === 0 ? "CORRECTED" : "INCOMPLETE";
  const firstPending = points.map(p => p.hotCorrection).find(h => h && h.status === "INCOMPLETE");

  report.thermalCorrection = {
    ...base,
    status,
    method: "METHOD_A",
    standardCode: METHOD_A_LABEL,
    procedureCode: procedureCode || null,
    correctedPoints: corrected,
    pendingPoints: pending,
    message: status === "CORRECTED" ? "" : points.length === 0 ? "Adicione os pontos de medição." : (firstPending?.message ?? "")
  };

  return report;
}


function methodASnapshotFor(
  report: MeReport,
  point: MeResultPoint,
  emq: number | null,
  temperatureC: number | null,
  procedure: NdtProcedure | null,
  vsa: number | null,
  /** k — FIXO (1,0 m/s/°C), nunca ausente: ver chamada em `recomputeMeThermalCorrection`. */
  k: number,
  /** Temperatura de Referência — FIXA (30 °C), nunca ausente: idem. */
  tref: number,
  procedureCode: string,
  options: { now: string; userId?: string | null }
): MeHotCorrectionSnapshot {
  const deltaT = temperatureC !== null ? temperatureC - tref : null;
  const snapshot: MeHotCorrectionSnapshot = {
    method: "METHOD_A",
    status: "INCOMPLETE",
    hotMeasuredThicknessMm: emq,
    measurementTemperatureC: temperatureC,
    referenceTemperatureC: tref,
    deltaTemperatureC: deltaT,
    referenceVelocityMs: vsa,
    coefficientK: k,
    correctionFactor: null,
    approximateThicknessMm: null,
    materialFamilyCode: report.materialFamilyCode,
    materialSpecification: report.materialSpecification || "",
    procedureId: report.procedureId,
    procedureCode,
    procedureRevision: procedure?.revision ?? report.procedureRevision ?? "",
    deviceAssetId: report.deviceAssetId,
    transducerAssetId: report.transducerAssetId,
    couplantId: report.couplantId,
    message: "",
    calculatedAt: options.now,
    calculatedBy: options.userId ?? null
  };

  // k e Tref são constantes fixas (nunca ausentes) — só Vsa ainda depende do
  // Procedimento vinculado, e é o único dos três que pode faltar.
  const missing: string[] = [];
  if (!report.procedureId) missing.push("Procedimento vinculado ao relatório");
  else if (!procedure) missing.push("Procedimento vinculado não encontrado no cadastro");
  else if (vsa === null) missing.push("Vsa — Velocidade Ultrassônica de Referência (m/s), no Procedimento");
  if (temperatureC === null) missing.push("Temperatura da Peça");
  if (emq === null) missing.push("Emq — Espessura Medida a Quente do ponto");

  if (missing.length > 0) {
    snapshot.message = `${METHOD_A_MISSING_DATA_MESSAGE} (${notFoundDetails(report, point, emq)}) Informe: ${missing.join(", ")}.`;
    return snapshot;
  }

  // vsa, temperatureC, emq e deltaT são todos não-nulos aqui (k e tref já são number, não precisam de "!").
  const factor = (vsa! - k * deltaT!) / vsa!;
  snapshot.correctionFactor = factor;
  snapshot.approximateThicknessMm = emq! * factor;
  snapshot.status = "CORRECTED";
  return snapshot;
}

/** Material, temperatura, espessura, aparelho e transdutor — o que a pessoa precisa para corrigir. */
function notFoundDetails(report: MeReport, point: MeResultPoint, emq: number | null): string {
  const thickness = typeof point.nominalThickness === "number" && point.nominalThickness > 0 ? point.nominalThickness : emq;
  const material = [report.materialFamilyLabel || materialFamilyLabel(report.materialFamilyCode), report.materialSpecification].filter(Boolean).join(" — ");
  return [
    `Material: ${material || "não informado"}`,
    `Temperatura: ${report.measurementTemperatureC ?? "—"} °C`,
    `Espessura: ${thickness !== null ? `${thickness.toFixed(2).replace(".", ",")} mm` : "não informada"}`,
    `Aparelho: ${[report.deviceManufacturer, report.deviceModel, report.deviceSerialNumber].filter(Boolean).join(" ") || report.deviceAssetId || "não informado"}`,
    `Transdutor: ${[report.transducerManufacturer, report.transducerModel, report.transducerIdentification].filter(Boolean).join(" ") || report.transducerAssetId || "não informado"}`
  ].join(" · ");
}

/** `true` quando este relatório está no fluxo de alta temperatura. */
export function isMeHighTemperature(report: MeReport): boolean {
  return isHighTemperatureMeasurement(report.measurementTemperatureC);
}

/**
 * A espessura que o laudo e os cálculos devem usar neste ponto: a ECT em
 * alta temperatura, a medida no fluxo convencional.
 *
 * Em alta temperatura SEM ECT devolve null — e o laudo, sem espessura atual,
 * fica "P — EM ELABORAÇÃO". É o comportamento correto: sem a correção
 * devida, aprovar ou reprovar o ponto seria um juízo sobre um número que
 * ninguém garantiu.
 */
export function meEffectiveThickness(report: MeReport, point: MeResultPoint): number | null {
  const measured = typeof point.actualThickness === "number" && Number.isFinite(point.actualThickness)
    ? point.actualThickness
    : null;

  if (!isMeHighTemperature(report)) return measured;

  return typeof point.correctedThickness === "number" && Number.isFinite(point.correctedThickness)
    ? point.correctedThickness
    : null;
}

/**
 * Validação dos dois campos no momento de liberar: só código do catálogo e
 * só inteiro de 0 ao máximo do perfil (550 °C) chegam ao documento. Texto fora do catálogo é
 * recusado — é o "não permitir salvar texto não existente" do comando, no
 * lugar em que ele vale para o servidor e não só para a tela.
 */
export function validateMeMaterialAndTemperature(report: MeReport): MePendency[] {
  const pendencies: MePendency[] = [];

  if (report.materialFamilyCode && !isValidMaterialFamilyCode(report.materialFamilyCode)) {
    pendencies.push({
      item: 2,
      field: "materialFamilyCode",
      message: "A Família de Material registrada não existe no catálogo. Selecione uma opção da lista."
    });
  }
  if (report.measurementTemperatureC !== null && report.measurementTemperatureC !== undefined
      && !isValidMeasurementTemperatureValue(report.measurementTemperatureC)) {
    pendencies.push({
      item: 2,
      field: "measurementTemperatureC",
      message: `A Temperatura da Medição deve ser um valor inteiro entre ${ME_TEMPERATURE_MIN_C} °C e ${ME_TEMPERATURE_MAX_C} °C.`
    });
  }
  // Alta temperatura sem correção possível não impede salvar rascunho, mas
  // precisa aparecer como pendência: o documento sairia com ECT vazia. Uma
  // mensagem por MOTIVO (não por ponto) — dez pontos sem calibração são um
  // problema só, e repeti-lo dez vezes esconderia os outros.
  if (isMeHighTemperature(report)) {
    const seen = new Set<string>();
    for (const point of Array.isArray(report.points) ? report.points : []) {
      const hot = point.hotCorrection;
      if (!hot || hot.status === "CORRECTED") continue;
      const key = hot.message;
      if (seen.has(key)) continue;
      seen.add(key);
      pendencies.push({ item: 2, field: "materialFamilyCode", message: hot.message });
    }
  }

  return pendencies;
}

function isValidMeasurementTemperatureValue(value: number): boolean {
  return Number.isInteger(value) && value >= ME_TEMPERATURE_MIN_C && value <= ME_TEMPERATURE_MAX_C;
}

/**
 * Os quatro blocos obrigatórios do Relatório de ME, na mesma numeração dos
 * cartões da tela (01 a 04). São os ÚNICOS impeditivos para liberar o ME ao
 * PLH — os cartões 00 e 05 a 08 (contexto, legenda, croqui, observações e
 * signatários) não bloqueiam a liberação.
 */
export type MeReportItem = 1 | 2 | 3 | 4;

export const ME_ITEM_LABELS: Readonly<Record<MeReportItem, string>> = {
  1: "ITEM 1 — IDENTIFICAÇÃO DO RELATÓRIO",
  2: "ITEM 2 — PARÂMETROS DO ENSAIO",
  3: "ITEM 3 — APARELHO / TRANSDUTOR",
  4: "ITEM 4 — RESULTADOS DAS MEDIÇÕES"
};

export interface MePendency {
  readonly item: MeReportItem;
  /** Campo real do relatório — permite levar o usuário direto ao campo. */
  readonly field: string;
  readonly message: string;
}

export interface MeReleaseValidation {
  readonly valid: boolean;
  readonly pendencies: readonly MePendency[];
}

/**
 * Completude dos ITENS 1 a 4 — a regra única usada tanto pelo botão de
 * validação da tela quanto pelo gate de liberação ao PLH (serviço e
 * backend). Cada pendência sai identificada pelo item e pelo campo, para a
 * tela poder listar "ITEM 2 • campo faltante" e navegar até ele.
 *
 * Nada aqui é campo novo: são exatamente os dados que os cartões 01 a 04 já
 * coletam hoje.
 */
export function validateMeItems(report: MeReport): MeReleaseValidation {
  const pendencies: MePendency[] = [];
  const add = (item: MeReportItem, field: string, message: string) => pendencies.push({ item, field, message });
  // Um validador nunca pode quebrar com rascunho parcial: campo ausente é
  // campo vazio, e vira pendência — não exceção.
  const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

  // ITEM 1 — IDENTIFICAÇÃO DO RELATÓRIO
  if (!text(report.equipmentTag)) add(1, "equipmentTag", "Informe o Equipamento / TAG.");
  if (!text(report.testDate)) add(1, "testDate", "Informe a Data do Ensaio.");
  if (!report.inspectorProfessionalId && !text(report.inspectorName)) {
    add(1, "inspectorProfessionalId", "Informe o Inspetor responsável pelo ensaio.");
  }

  // ITEM 2 — PARÂMETROS DO ENSAIO
  if (!report.procedureId && !text(report.procedureRevision)) add(2, "procedureId", "Informe o Procedimento do ensaio.");
  if (!report.referenceNormId && !text(report.referenceNorm)) add(2, "referenceNormId", "Informe a Norma de Referência.");
  if (!report.acceptanceCriteriaId && !text(report.acceptanceCriteria)) add(2, "acceptanceCriteriaId", "Informe o Critério de Aceitação.");
  // Família de Material / TME fora do catálogo, e alta temperatura sem regra
  // cadastrada, são pendências do ITEM 2 — mesma regra no servidor e na tela.
  for (const pendency of validateMeMaterialAndTemperature(report)) pendencies.push(pendency);

  // ITEM 3 — APARELHO / TRANSDUTOR
  if (text(report.deviceManufacturer) && !text(report.deviceCalibrationCert)) {
    add(3, "deviceCalibrationCert", "O aparelho não possui certificado de calibração informado.");
  }
  // Calibração vencida nunca impede salvar rascunho — impede liberar.
  if (isCalibrationExpired(report.deviceCalibrationValidity)) {
    add(3, "deviceCalibrationValidity", "O Aparelho de ME-US selecionado possui certificado de calibração vencido.");
  }
  if (isCalibrationExpired(report.calibrationBlockCertValidity)) {
    add(3, "calibrationBlockCertValidity", "O Bloco de Calibração selecionado possui certificado vencido.");
  }

  // ITEM 4 — RESULTADOS DAS MEDIÇÕES
  const points = Array.isArray(report.points) ? report.points : [];
  if (points.length === 0) {
    add(4, "points", "Adicione ao menos um ponto de medição na seção Resultados.");
  } else {
    const highTemperature = isMeHighTemperature(report);
    const measuredLabel = highTemperature ? "Emq — Espessura Medida a Quente" : "Espessura Atual";
    const withoutActual = points.filter(p => p.actualThickness === null || p.actualThickness === undefined).length;
    if (withoutActual > 0) add(4, "actualThickness", `Existem ${withoutActual} ${withoutActual === 1 ? "ponto" : "pontos"} sem ${measuredLabel}.`);
    // Em alta temperatura o que alimenta o laudo é a Ea (MÉTODO A). Um ponto
    // medido mas sem correção sairia com ESP. ATUAL vazia no documento — é pendência.
    if (highTemperature) {
      const withoutCorrected = points.filter(p =>
        p.actualThickness !== null && p.actualThickness !== undefined &&
        (p.correctedThickness === null || p.correctedThickness === undefined)
      ).length;
      if (withoutCorrected > 0) {
        add(4, "correctedThickness", `Existem ${withoutCorrected} ${withoutCorrected === 1 ? "ponto" : "pontos"} sem Ea — Espessura Aproximada (MÉTODO A).`);
      }
    }
    const withoutComponent = points.filter(p => !text(p.component)).length;
    if (withoutComponent > 0) add(4, "component", `Existem ${withoutComponent} ${withoutComponent === 1 ? "ponto" : "pontos"} sem Componente informado.`);
    const withoutLocation = points.filter(p => !text(p.location)).length;
    if (withoutLocation > 0) add(4, "location", `Existem ${withoutLocation} ${withoutLocation === 1 ? "ponto" : "pontos"} sem Localização informada.`);
  }

  return { valid: pendencies.length === 0, pendencies };
}

/** Pendências agrupadas por item — só os itens que realmente têm pendência. */
export function groupMePendencies(pendencies: readonly MePendency[]): Array<{ item: MeReportItem; label: string; messages: string[] }> {
  const groups: Array<{ item: MeReportItem; label: string; messages: string[] }> = [];
  for (const item of [1, 2, 3, 4] as MeReportItem[]) {
    const messages = pendencies.filter(p => p.item === item).map(p => p.message);
    if (messages.length > 0) groups.push({ item, label: ME_ITEM_LABELS[item], messages });
  }
  return groups;
}

/**
 * "Validar Dados" (section 29 of the ME command): a pure completeness check,
 * never a status transition. Messages must name the specific missing field —
 * never a generic "Dados inválidos."
 *
 * É deliberadamente MENOS restritivo que o gate de liberação ao PLH: valida a
 * consistência da MEDIÇÃO (identificação, aparelho e resultados) para o
 * inspetor conferir o trabalho em campo, sem exigir ainda os parâmetros
 * documentais do ITEM 2. Quem exige os quatro itens é `validateMeItems`, no
 * momento de liberar o relatório.
 */
export function validateMeReportCompleteness(report: MeReport): MeValidationResult {
  const { pendencies } = validateMeItems(report);
  const errors = pendencies.filter(p => p.item !== 2).map(p => p.message);
  return { valid: errors.length === 0, errors };
}
